// 권위:
//   - dashboard/api/.../TradingController.java — GET /api/v1/trading/positions, /unfilled
//   - dashboard/api/.../TradingDtos.java — 응답 schema
//   - tradingMappers.ts — raw → UI 매핑
//
// 본 모듈은 trading 화면 2건 (Positions / Unfilled) 의 실 API 호출 layer.
// 다른 화면 (eventsApi.ts / governanceApi.ts 등) 의 useQuery 패턴 정합:
//   - axios `apiGet` 호출 (envelope 자동 언랩, 401 refresh 자동 처리)
//   - useQuery hook export (queryKey + queryFn)
//   - staleTime 30s (ARCH §13.4 성과 데이터 정합)

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '../../api/client';
import type { PositionRow, UnfilledOrderRow } from './tradingMock';
import {
  toUiPosition,
  toUiUnfilled,
  type ApiPositionRow,
  type ApiUnfilledRow,
} from './tradingMappers';

// ── 응답 envelope (자동 언랩 후 형태) ─────────────────────────

interface PositionListResponse {
  positions: ApiPositionRow[];
  total: number;
  asOf: string;
}

interface UnfilledListResponse {
  unfilled: ApiUnfilledRow[];
  total: number;
  asOf: string;
}

// ── REST 호출 ──────────────────────────────────────────────

export interface PositionsQuery {
  state?: string;
  symbol?: string;
  limit?: number;
  offset?: number;
}

export async function fetchPositions(query: PositionsQuery = {}): Promise<{
  rows: PositionRow[];
  total: number;
  asOf: string;
}> {
  const params: Record<string, string | number> = {};
  if (query.state) params.state = query.state;
  if (query.symbol) params.symbol = query.symbol;
  if (query.limit !== undefined) params.limit = query.limit;
  if (query.offset !== undefined) params.offset = query.offset;
  const data = await apiGet<PositionListResponse>('/api/v1/trading/positions', { params });
  return {
    rows: (data.positions ?? []).map(toUiPosition),
    total: data.total ?? 0,
    asOf: data.asOf,
  };
}

export interface UnfilledQuery {
  stage?: string;
  symbol?: string;
  limit?: number;
  offset?: number;
}

export async function fetchUnfilledOrders(query: UnfilledQuery = {}): Promise<{
  rows: UnfilledOrderRow[];
  total: number;
  asOf: string;
}> {
  const params: Record<string, string | number> = {};
  if (query.stage) params.stage = query.stage;
  if (query.symbol) params.symbol = query.symbol;
  if (query.limit !== undefined) params.limit = query.limit;
  if (query.offset !== undefined) params.offset = query.offset;
  const data = await apiGet<UnfilledListResponse>('/api/v1/trading/unfilled', { params });
  return {
    rows: (data.unfilled ?? []).map(toUiUnfilled),
    total: data.total ?? 0,
    asOf: data.asOf,
  };
}

// ── React Query hooks ─────────────────────────────────────

export function useTradingPositionsQuery(
  query: PositionsQuery = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<{ rows: PositionRow[]; total: number; asOf: string }, Error> {
  return useQuery({
    queryKey: ['trading', 'positions', query],
    queryFn: () => fetchPositions(query),
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}

export function useTradingUnfilledQuery(
  query: UnfilledQuery = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<{ rows: UnfilledOrderRow[]; total: number; asOf: string }, Error> {
  return useQuery({
    queryKey: ['trading', 'unfilled', query],
    queryFn: () => fetchUnfilledOrders(query),
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}
